import {GeoLocation} from "api/geoLocation";
import defaultHubIcon from "components/src/Icons/Hub/default-hub.svg";
import operatorActiveHubIcon from "components/src/Icons/Hub/operator-active-hub.svg";
import operatorInactiveHubIcon from "components/src/Icons/Hub/operator-inactive-hub.svg";
import carrierActiveHubIcon from "components/src/Icons/Hub/carrier-active-hub.svg";
import carrierInactiveHubIcon from "components/src/Icons/Hub/carrier-inactive-hub.svg";
import mapboxgl, {FullscreenControl, GeoJSONSourceRaw} from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import ReactMapboxGl, {Layer, Source,} from "react-mapbox-gl";
import {MapEvent} from "react-mapbox-gl/lib/map-events";
import {apiMapboxAccessToken} from "../../../api/configuration";
import {MarkerPopup} from "../HubMarker/HubMarkerPopup";
import {MapScaleControl} from "components/src/Component/MapScaleControl";
import {Box, Slider} from "@material-ui/core";

const styles = {
  londonCycle: "mapbox://styles/mapbox/light-v9",
  light: "mapbox://styles/mapbox/light-v9",
  dark: "mapbox://styles/mapbox/dark-v10",
  basic: "mapbox://styles/mapbox/basic-v9",
  outdoor: "mapbox://styles/mapbox/outdoors-v10",
  street: "mapbox://styles/mapbox/streets-v11",
};

const Mapbox = ReactMapboxGl({
  accessToken: apiMapboxAccessToken,
});

const mapStyle = {
  width: "500px",
  height: "282px",
  marginLeft: "30px",
};

interface HeatmapProps {
  onStyleLoad?: (map: any) => any;
  pickupPoints: any;
  deliveryPoints: any;
  southWestBound: GeoLocation;
  northEastBound: GeoLocation;
}

const MIN_MAX_COLIS = 10;

export const Heatmap = (props: HeatmapProps) => {
  const { pickupPoints, deliveryPoints, southWestBound, northEastBound } = props;

  const parsedDeliveryPoints = useMemo(() =>
    JSON.parse(deliveryPoints) as GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>,
    [deliveryPoints]
  );

  const maxNbColisInData = useMemo(() => {
    let max = 0;
    for (const feature of parsedDeliveryPoints.features) {
      const nbColis = feature.properties?.nbColis ?? 0;
      if (nbColis > max) max = nbColis;
    }
    return Math.max(max, MIN_MAX_COLIS);
  }, [parsedDeliveryPoints]);

  const sliderMax = maxNbColisInData * 2

  const [maxColis, setMaxColis] = useState<number | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    if (mapRef.current) {
      // Let the DOM update before resizing the map canvas
      setTimeout(() => mapRef.current?.resize(), 100);
    }
  }, [isFullscreen]);

  const fitBounds = useMemo((): [[number, number], [number, number]] => [
    [southWestBound.lon, southWestBound.lat],
    [northEastBound.lon, northEastBound.lat],
  ], [southWestBound.lon, southWestBound.lat, northEastBound.lon, northEastBound.lat]);
  const effectiveMaxColis = maxColis ?? maxNbColisInData;

  const operatorActiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const operatorInactiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const carrierActiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const carrierInactiveHubs: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  const parsedPickupPoints: GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties> = {features: [], type: "FeatureCollection"};
  
  (JSON.parse(pickupPoints) as GeoJSON.FeatureCollection<GeoJSON.Geometry, GeoJSON.GeoJsonProperties>).features.forEach((pickupPoint: GeoJSON.Feature<GeoJSON.Geometry>) => {
    const type = pickupPoint?.properties?.hubType ?? null
    switch (type) {
      case "OPERATOR_ACTIVE_HUB":
        operatorActiveHubs.features.push(pickupPoint)
        break;
      case "OPERATOR_INACTIVE_HUB":
        operatorInactiveHubs.features.push(pickupPoint)
        break;
      case "CARRIER_ACTIVE_HUB":
        carrierActiveHubs.features.push(pickupPoint)
        break;
      case "CARRIER_INACTIVE_HUB":
        carrierInactiveHubs.features.push(pickupPoint)
        break;
      default:
        parsedPickupPoints.features.push(pickupPoint)
        break;
    }
  });

  const operatorActiveHubsSource: GeoJSONSourceRaw = {
    data: operatorActiveHubs,
    type: "geojson",
  };

  const operatorInactiveHubsSource: GeoJSONSourceRaw = {
    data: operatorInactiveHubs,
    type: "geojson",
  };

  const carrierActiveHubsSource: GeoJSONSourceRaw = {
    data: carrierActiveHubs,
    type: "geojson",
  };

  const carrierInactiveHubsSource: GeoJSONSourceRaw = {
    data: carrierInactiveHubs,
    type: "geojson",
  };

  const pickupPointsSource: GeoJSONSourceRaw = {
    data: parsedPickupPoints,
    type: "geojson",
  };

  const deliveryPointsSource: GeoJSONSourceRaw = {
    data: parsedDeliveryPoints,
    type: "geojson",
  };

  const pickupImg = new Image(25, 25);
  pickupImg.src = defaultHubIcon;

  const operatorActiveHubImg = new Image(25, 25);
  operatorActiveHubImg.src = operatorActiveHubIcon;

  const operatorInactiveHubImg = new Image(25, 25);
  operatorInactiveHubImg.src = operatorInactiveHubIcon;

  const carrierActiveHubImg = new Image(25, 25);
  carrierActiveHubImg.src = carrierActiveHubIcon;

  const carrierInactiveHubImg = new Image(25, 25);
  carrierInactiveHubImg.src = carrierInactiveHubIcon;

  const onStyleLoad: MapEvent = (map) => {
    const { onStyleLoad } = props;

    mapRef.current = map;
    map.addImage("pickup-point", pickupImg);
    map.addImage("operator-active-hub", operatorActiveHubImg);
    map.addImage("operator-inactive-hub", operatorInactiveHubImg);
    map.addImage("carrier-active-hub", carrierActiveHubImg);
    map.addImage("carrier-inactive-hub", carrierInactiveHubImg);
    map.addControl(new FullscreenControl({ container: wrapperRef.current! }));

    return onStyleLoad && onStyleLoad(map);
  };

  const heatmapLayer = useMemo(() => ({
    // increase weight as diameter breast height increases
    "heatmap-weight": {
      property: "nbColis",
      type: "exponential",
      stops: [
        [1, 0],
        [effectiveMaxColis * 0.8, 0.8],
        [effectiveMaxColis, 1],
      ],
    },
    // assign color values be applied to points depending on their density
    "heatmap-color": [
      "interpolate",
      ["linear"],
      ["heatmap-density"],
      0,
      "rgba(255,255,178,0)",
      0.02,
      "rgb(0,40,169)",
      0.05,
      "rgb(37,162,204)",
      0.1,
      "rgb(49,210,141)",
      0.2,
      "rgba(177,225,5,0.7)",
      0.4,
      "rgba(253,141,60,0.8)",
      0.6,
      "rgba(240,59,32,0.9)",
      0.8,
      "rgba(189,0,38,1)",
    ],
    "heatmap-radius": {
      stops: [
        [5, 5],
        [7, 15],
        [10, 17],
        [12, 20],
      ],
    },
    "heatmap-intensity": {
      stops: [
        [5, 1],
        [7, 2],
        [10, 4],
        [12, 6],
      ],
    },
  }), [effectiveMaxColis]);

  // Color stops as proportions of effectiveMaxColis: ~2%, ~3%, ~8%, ~17%, ~33%, ~50%, 100%
  const colorStops = useMemo(() => [
    Math.round(effectiveMaxColis * 0.02),
    Math.round(effectiveMaxColis * 0.03),
    Math.round(effectiveMaxColis * 0.08),
    Math.round(effectiveMaxColis * 0.17),
    Math.round(effectiveMaxColis * 0.33),
    Math.round(effectiveMaxColis * 0.5),
    effectiveMaxColis,
  ], [effectiveMaxColis]);

  const markerLayer = useMemo(() => ({
    "circle-radius": {
      property: "nbColis",
      type: "exponential",
      stops: [
        [{ zoom: 22, value: colorStops[0] }, 4],
        [{ zoom: 22, value: colorStops[1] }, 5],
        [{ zoom: 22, value: colorStops[2] }, 6],
        [{ zoom: 22, value: colorStops[3] }, 7],
        [{ zoom: 22, value: colorStops[4] }, 8],
        [{ zoom: 22, value: colorStops[5] }, 9],
        [{ zoom: 22, value: colorStops[6] }, 10],
        [{ zoom: 22, value: Math.round(effectiveMaxColis * 1.5) }, 12],
      ],
    },
    "circle-color": {
      property: "nbColis",
      type: "exponential",
      stops: [
        [0, "rgba(236,222,239,0)"],
        [colorStops[0], "rgb(0,40,169)"],
        [colorStops[1], "rgb(37,162,204)"],
        [colorStops[2], "rgb(49,210,141)"],
        [colorStops[3], "rgba(177,225,5,0.7)"],
        [colorStops[4], "rgba(253,141,60,0.8)"],
        [colorStops[5], "rgba(240,59,32,0.9)"],
        [colorStops[6], "rgba(189,0,38,1)"],
      ],
    },
    "circle-stroke-color": "white",
    "circle-stroke-width": 1,
    "circle-opacity": {
      stops: [
        [7, 0],
        [8, 1],
      ],
    },
  }), [colorStops, effectiveMaxColis]);

  if (!mapboxgl.supported()) {
    return <Box className={mapStyle}>
      Votre navigateur ne supporte pas la visualisation de carte.
    </Box>
  }

  const fullscreenMapStyle = {
    width: "100%",
    flex: 1,
  };

  const fullscreenWrapperStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    background: "#fff",
  };

  const fullscreenScaleStyle = {
    margin: "6px 30px 8px",
    fontSize: "10px",
  };

  return (
    <div ref={wrapperRef} style={isFullscreen ? fullscreenWrapperStyle : undefined}>
    <Mapbox
      style={styles.street}
      fitBounds={fitBounds}
      fitBoundsOptions={{ padding: 15 }}
      containerStyle={isFullscreen ? fullscreenMapStyle : mapStyle}
      onStyleLoad={onStyleLoad}
    >
      <Source id="heatmap" geoJsonSource={deliveryPointsSource} />
      <Layer
        id="delivery-marker"
        type="circle"
        sourceId="heatmap"
        paint={markerLayer}
        minZoom={13}
      />
      <Layer
        id="marker"
        type="heatmap"
        sourceId="heatmap"
        paint={heatmapLayer}
        maxZoom={13}
      />
      <Source id="pickup" geoJsonSource={pickupPointsSource} />
      <Layer
        id="pickup-marker"
        type="symbol"
        layout={{ "icon-image": "pickup-point" }}
        sourceId="pickup"
      />
      <Source id="operator-active-hub" geoJsonSource={operatorActiveHubsSource} />
      <Layer
        id="operator-active-hub-marker"
        type="symbol"
        layout={{ "icon-image": "operator-active-hub" }}
        sourceId="operator-active-hub"
      />
      <Source id="operator-inactive-hub" geoJsonSource={operatorInactiveHubsSource} />
      <Layer
        id="operator-inactive-hub-marker"
        type="symbol"
        layout={{ "icon-image": "operator-inactive-hub" }}
        sourceId="operator-inactive-hub"
      />
      <Source id="carrier-active-hub" geoJsonSource={carrierActiveHubsSource} />
      <Layer
        id="carrier-active-hub-marker"
        type="symbol"
        layout={{ "icon-image": "carrier-active-hub" }}
        sourceId="carrier-active-hub"
      />
      <Source id="carrier-inactive-hub" geoJsonSource={carrierInactiveHubsSource} />
      <Layer
        id="carrier-inactive-hub-marker"
        type="symbol"
        layout={{ "icon-image": "carrier-inactive-hub" }}
        sourceId="carrier-inactive-hub"
      />
      <MapScaleControl />
      <MarkerPopup />
    </Mapbox>
    <div style={isFullscreen ? fullscreenScaleStyle : { marginLeft: "30px", marginTop: "6px", width: "500px", fontSize: "10px" }}>
      <div style={{
        height: "8px",
        borderRadius: "2px",
        background: "linear-gradient(to right, rgb(0,40,169), rgb(37,162,204), rgb(49,210,141), rgb(177,225,5), rgb(253,141,60), rgb(240,59,32), rgb(189,0,38))",
      }} />
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2px", color: "#666" }}>
        {colorStops.map((value, index) => (
          <span key={index}>{index === colorStops.length - 1 ? `${value}+` : value}</span>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
        <span style={{ color: "#666", whiteSpace: "nowrap", minWidth: "100px" }}>Max: <strong>{effectiveMaxColis}</strong> colis</span>
        <Slider
          value={effectiveMaxColis}
          onChange={(_, value) => setMaxColis(value as number)}
          min={MIN_MAX_COLIS}
          max={sliderMax}
          step={Math.max(1, Math.round(sliderMax / 100))}
          style={{ flex: 1 }}
        />
      </div>
    </div>
    </div>
  );
};
